import type { Dayjs } from 'dayjs';
import { InputAdornment } from '@mui/material';
import { Controller, type Control, type FieldPath } from 'react-hook-form';
import { VyDatePicker, VyIconButton, VyStack, VyTextField, VyTypography, Delete } from '@vy/ui-design-system';
import { isEmptyString, isNotNullOrUndefined, vyDate } from '@vy/utils';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';

type InstallmentRowProps = {
  index: number;
  control: Control<PlanInputForm>;
  datePath: FieldPath<PlanInputForm>;
  amountPath: FieldPath<PlanInputForm>;
  percentageOfTotal: number;
  currencySymbol: string;
  /** Other installments' dates (ISO) — disabled in this row's picker to prevent duplicates. */
  usedDates: string[];
  contractStartDate: string;
  contractEndDate?: string;
  /** Omit to disable removal (e.g. when this is the only installment). */
  onRemove?: () => void;
};

export const InstallmentRow: React.FC<InstallmentRowProps> = ({
  index,
  control,
  datePath,
  amountPath,
  percentageOfTotal,
  currencySymbol,
  usedDates,
  contractStartDate,
  contractEndDate,
  onRemove,
}) => {
  return (
    <VyStack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
      <VyTypography variant="body2" sx={(theme) => ({ width: theme.spacing(14.5), flexShrink: 0 })}>
        Installment no. {index + 1}
      </VyTypography>

      <VyStack sx={{ flex: 1, minWidth: 0 }}>
        <Controller
          control={control}
          name={datePath}
          render={({ field, fieldState }) => (
            <VyDatePicker
              value={field.value ? vyDate(field.value) : null}
              onChange={(newDate: Dayjs | null) =>
                field.onChange(
                  isNotNullOrUndefined(newDate)
                    ? vyDate(newDate.toDate(), { keepLocalTime: true }).startOf('day').toISOString()
                    : undefined,
                )
              }
              shouldDisableDate={(date: Dayjs) =>
                shouldDisableInstallmentDate(date, usedDates, field.value, contractStartDate, contractEndDate)
              }
              error={isNotNullOrUndefined(fieldState.error)}
              helperText={fieldState.error?.message}
              data-vy={`plan-charge-drawer-installment-date-${index}`}
            />
          )}
        />
      </VyStack>

      <VyStack sx={{ flex: 1, minWidth: 0 }}>
        <Controller
          control={control}
          name={amountPath}
          render={({ field, fieldState }) => (
            <VyTextField
              value={field.value ?? ''}
              onChange={(event) => {
                const raw = event.target.value;
                if (isEmptyString(raw)) {
                  field.onChange(0);
                  return;
                }
                const parsed = Number(raw);
                field.onChange(Number.isNaN(parsed) ? 0 : parsed);
              }}
              onBlur={field.onBlur}
              type="number"
              error={isNotNullOrUndefined(fieldState.error)}
              helperText={fieldState.error?.message}
              slotProps={{
                input: { startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> },
                htmlInput: { min: 0 },
              }}
              data-vy={`plan-charge-drawer-installment-amount-${index}`}
            />
          )}
        />
      </VyStack>

      <VyTypography variant="body2" sx={(theme) => ({ width: theme.spacing(7), flexShrink: 0, textAlign: 'right' })}>
        {percentageOfTotal.toFixed(2)}%
      </VyTypography>

      <VyIconButton
        aria-label={`Remove installment ${index + 1}`}
        disabled={!onRemove}
        onClick={onRemove}
        data-vy={`plan-charge-drawer-installment-remove-${index}`}
      >
        <Delete width={18} height={18} />
      </VyIconButton>
    </VyStack>
  );
};

function shouldDisableInstallmentDate(
  date: Dayjs,
  usedDates: string[],
  currentValue: string | undefined,
  contractStartDate: string,
  contractEndDate?: string,
): boolean {
  const current = vyDate(date.toDate());
  const isBeforeStart = current.isBefore(vyDate(contractStartDate), 'day');
  const isAfterEnd = isNotNullOrUndefined(contractEndDate) && current.isAfter(vyDate(contractEndDate), 'day');
  // Block dates already taken by *other* rows, but keep this row's own date pickable.
  const isTakenByAnother = usedDates.some(
    (used) => used !== currentValue && vyDate(used).isSame(current, 'day'),
  );
  return isBeforeStart || Boolean(isAfterEnd) || isTakenByAnother;
}
